import { EventEmitter, Injectable } from '@angular/core';

export enum Color {
  Default = '#484949',
  Error = '#B94863',
  Success = '#549F3A',
  Info = '#CBE3FC',
  Warning = '#B69018',
}

export interface SnackbarItem {
  message: string;
  actions?: { label: string; callback?: () => void; icon?: string }[];
  duration?: number;
  timeout?: ReturnType<typeof setTimeout>;
  color?: Color;
}

/**
 * A service that manages the snackbar.
 * When you register to SnackbarService you can access the snackbar service and use it to show the snackbar.
 *
 * This code includes the "SnackbarService" class, which is used to manage snackbar items in an Angular application. The service provides the necessary functions and data to manage snackbar items.
 *
 * The class is marked with the "@Injectable" decorator, indicating that it is registered with the "root" injector and can be injected by any component. It also implements the "OnDestroy" lifecycle interface.
 *
 * The class features an array "snackbars" that contains open snackbar items and an "EventEmitter" called "listener$". The service also provides various methods to add a new snackbar item, close a snackbar, set a timer, clear a timer, and retrieve previous snackbar actions.
 *
 * The "show" method of the service creates a snackbar item and adds it to the list of open snackbar items. The "starTimeout" method sets a timer for a snackbar item, and the "clearTimeout" method clears the timer. The "performAction" method triggers the execution of an action in the snackbar item. The "getSnackbars" method returns all open snackbar items.
 *
 * Finally, since the service implements the "OnDestroy" lifecycle interface, the "ngOnDestroy" method clears timers in open snackbar items.
 */
@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  public snackbars: any[] = [];
  public listener$ = new EventEmitter<SnackbarItem>();

  // ngOnDestroy(): void {
  //   this.snackbars.forEach((snackbar) => {
  //     this.clearTimeout(snackbar);
  //   });
  // }

  /* Adding a new snackbar to the snackbars array and display the notification on the page */
  show({ message, duration, actions, color }: SnackbarItem): void {
    const snackbar: SnackbarItem = {
      message,
      duration,
      actions,
      color,
    };
    this.snackbars.push(snackbar);
    this.starTimeout(snackbar);
  }

  /* Removing the snackbar from the snackbars array. */
  close(snackbar: SnackbarItem): void {
    const index = this.snackbars.indexOf(snackbar);
    if (index > -1) {
      this.listener$.emit(snackbar);
      this.snackbars.splice(index, 1);
    }
  }

  /* Setting a timeout for the snackbar to close. */
  starTimeout(snackbar: SnackbarItem): void {
    if (snackbar.duration) {
      snackbar.timeout = setTimeout(() => {
        this.close(snackbar);
      }, snackbar.duration);
    }
  }

  /* Clearing the timeout for the snackbar. */
  clearTimeout(snackbar: SnackbarItem): void {
    clearTimeout(snackbar.timeout);
  }

  /* Calling the callback function of the first action in the actions array. */
  performAction(snackbar: SnackbarItem, action: { label: string; callback: () => void }): void {
    if (action) {
      action.callback();
    }
    this.close(snackbar);
  }

  /* Returning the snackbars array. */
  getSnackbars(): SnackbarItem[] {
    return this.snackbars;
  }
}
